/**
 * Budget-reset anomaly dashboard — Zod schemas.
 *
 * Read surface: GET /api/v1/tenants/{tenant_id}/agents/budget-reset-anomalies. The detector is the
 * ONLY writer; this surface is observe-only. Every anomaly is one append-only row of the
 * tamper-evident ledger — a detected re-provision "reset" of a cumulative budget.
 *
 * The response is the contract's BudgetResetAnomalyList, generated into api.generated.ts by make docs;
 * this module never restates it.
 *
 * Semantics that drive the UI (from the Go detector):
 *   - carried_forward=true  → the ONLY caught case (reprovision_resume): the running total DID
 *                             survive across the lineage edge. Benign — muted in the fleet view.
 *   - carried_forward=false → the reset went SILENT (sever_via_delete / fresh_identity_suspected /
 *                             standing_delegation_reset). The control gap the operator must see.
 */

import { z } from "zod";

import { anomalyListResponseSchema, anomalySchema } from "./api.generated";

export { anomalyListResponseSchema, anomalySchema };
export type Anomaly = z.infer<typeof anomalySchema>;
export type AnomalyListResponse = z.infer<typeof anomalyListResponseSchema>;

// The four behavioral signatures of a budget reset and the detection confidence, exactly as the
// contract enumerates them.
export const anomalySignatureSchema = anomalySchema.shape.signature_kind;
export type AnomalySignature = z.infer<typeof anomalySignatureSchema>;
export const anomalyConfidenceSchema = anomalySchema.shape.confidence;
export type AnomalyConfidence = z.infer<typeof anomalyConfidenceSchema>;

// ── URL-as-source-of-truth list/filter state ────────────────────────────────
// Every value degrades to undefined on an unknown/hand-edited value (never hard-errors the route).
export const anomaliesSearchSchema = z.object({
  signature_kind: anomalySignatureSchema.optional().catch(undefined),
  confidence: anomalyConfidenceSchema.optional().catch(undefined),
  // Tri-state: undefined = any; false = the actionable un-caught resets (the default focus); true =
  // the benign caught rows. Coerced from the ?carried=... string so the URL stays shareable.
  carried_forward: z.boolean().optional().catch(undefined),
  // Half-open [from, to) detected_at window (YYYY-MM-DD or RFC3339). Scopes BOTH the dashboard
  // list and the CSV export to an audit period, so the export inherits exactly what is shown.
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  offset: z.coerce.number().int().nonnegative().optional().catch(undefined),
});
export type AnomaliesSearch = z.infer<typeof anomaliesSearchSchema>;
